import React, { useEffect, useState } from 'react';
import api from '../../services/api';

const randomId = (prefix) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const emptyCity = () => ({ id: randomId('city'), name: '', sub_regions: [], residences: [] });
const emptySubRegion = () => ({ id: randomId('sub'), name: '', residences: [] });

const CitiesManager = ({ open, onClose, onSave }) => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedCity, setExpandedCity] = useState(null);
  const [expandedSubRegion, setExpandedSubRegion] = useState(null);
  const [newResidenceInputs, setNewResidenceInputs] = useState({});
  const [newSubRegionInputs, setNewSubRegionInputs] = useState({});

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setExpandedCity(null);
    setExpandedSubRegion(null);
    setNewResidenceInputs({});
    setNewSubRegionInputs({});
    api.forms.getAdminCityCatalog()
      .then((res) => {
        const data = res?.data?.data?.cities || res?.data?.cities || [];
        setCities(data.map((c) => ({
          ...c,
          id: c.id || randomId('city'),
          sub_regions: (c.sub_regions || []).map((sr) => ({
            ...sr,
            id: sr.id || randomId('sub'),
            residences: Array.isArray(sr.residences) ? sr.residences : [],
          })),
          residences: Array.isArray(c.residences) ? c.residences : [],
        })));
      })
      .catch(() => { setCities([]); })
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const updateCity = (cityId, updater) => {
    setCities((prev) => prev.map((c) => (c.id === cityId ? updater(c) : c)));
  };

  const removeCity = (cityId) => {
    setCities((prev) => prev.filter((c) => c.id !== cityId));
  };

  const addCity = () => {
    const next = emptyCity();
    setCities((prev) => [...prev, next]);
    setExpandedCity(next.id);
  };

  const addSubRegion = (cityId) => {
    const next = emptySubRegion();
    updateCity(cityId, (c) => ({
      ...c,
      sub_regions: [...(c.sub_regions || []), next],
    }));
    setExpandedSubRegion(next.id);
  };

  const removeSubRegion = (cityId, subId) => {
    updateCity(cityId, (c) => ({
      ...c,
      sub_regions: (c.sub_regions || []).filter((sr) => sr.id !== subId),
    }));
  };

  const updateSubRegion = (cityId, subId, updater) => {
    updateCity(cityId, (c) => ({
      ...c,
      sub_regions: (c.sub_regions || []).map((sr) => (sr.id === subId ? updater(sr) : sr)),
    }));
  };

  const addResidenceToCity = (cityId) => {
    const inputKey = `city_${cityId}`;
    const name = (newResidenceInputs[inputKey] || '').trim();
    if (!name) return;
    updateCity(cityId, (c) => ({
      ...c,
      residences: [...(c.residences || []), name],
    }));
    setNewResidenceInputs((prev) => ({ ...prev, [inputKey]: '' }));
  };

  const removeResidenceFromCity = (cityId, idx) => {
    updateCity(cityId, (c) => ({
      ...c,
      residences: (c.residences || []).filter((_, i) => i !== idx),
    }));
  };

  const addResidenceToSubRegion = (cityId, subId) => {
    const inputKey = `sub_${subId}`;
    const name = (newResidenceInputs[inputKey] || '').trim();
    if (!name) return;
    updateSubRegion(cityId, subId, (sr) => ({
      ...sr,
      residences: [...(sr.residences || []), name],
    }));
    setNewResidenceInputs((prev) => ({ ...prev, [inputKey]: '' }));
  };

  const removeResidenceFromSubRegion = (cityId, subId, idx) => {
    updateSubRegion(cityId, subId, (sr) => ({
      ...sr,
      residences: (sr.residences || []).filter((_, i) => i !== idx),
    }));
  };

  const handleSave = async () => {
    const sanitized = cities
      .map((c) => {
        const name = (c.name || '').trim();
        if (!name) return null;
        const subRegions = (c.sub_regions || [])
          .map((sr) => ({
            name: (sr.name || '').trim(),
            residences: (sr.residences || []).filter((r) => r.trim()),
          }))
          .filter((sr) => sr.name);
        const hasSubRegions = subRegions.length > 0;
        return {
          name,
          ...(hasSubRegions
            ? { sub_regions: subRegions }
            : { residences: (c.residences || []).filter((r) => r.trim()) }),
        };
      })
      .filter(Boolean);

    setSaving(true);
    try {
      await api.forms.updateAdminCityCatalog({ cities: sanitized });
      onSave && onSave();
      onClose();
    } catch (err) {
      window.alert('Failed to save catalog: ' + (err?.response?.data?.message || err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const ResidencesList = ({ residences, onRemove }) => (
    <div style={{ marginTop: 4 }}>
      {residences.length === 0 && <span style={{ color: '#999', fontSize: 13 }}>No residences</span>}
      {residences.map((r, idx) => (
        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0' }}>
          <span style={{ flex: 1, fontSize: 14 }}>{r}</span>
          <button type="button" className="btn btn-danger" style={{ fontSize: 11, padding: '2px 6px' }} onClick={() => onRemove(idx)}>
            x
          </button>
        </div>
      ))}
    </div>
  );

  const ResidenceInput = ({ inputKey, placeholder, onAdd }) => (
    <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
      <input
        type="text"
        className="form-control"
        style={{ flex: 1, fontSize: 13 }}
        placeholder={placeholder}
        value={newResidenceInputs[inputKey] || ''}
        onChange={(e) => setNewResidenceInputs((prev) => ({ ...prev, [inputKey]: e.target.value }))}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAdd(); } }}
      />
      <button type="button" className="btn btn-outline" style={{ fontSize: 12, padding: '2px 8px' }} onClick={onAdd}>
        Add
      </button>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Manage City Catalog</h3>
          <button className="modal-close" onClick={onClose}>x</button>
        </div>

        <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {loading ? (
            <p>Loading catalog...</p>
          ) : cities.length === 0 ? (
            <p style={{ color: '#999' }}>No cities found. Add a city to get started.</p>
          ) : (
            cities.map((city) => {
              const isExpanded = expandedCity === city.id;
              const hasSubRegions = (city.sub_regions || []).length > 0;
              return (
                <article key={city.id} className="admin-form-builder-page" style={{ marginBottom: 12 }}>
                  <div className="admin-form-builder-page-head" style={{ cursor: 'pointer' }} onClick={() => setExpandedCity(isExpanded ? null : city.id)}>
                    <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 12, color: '#999' }}>{isExpanded ? '\u25BC' : '\u25B6'}</span>
                      {city.name || '<Unnamed city>'}
                      {hasSubRegions && <span style={{ fontSize: 11, color: '#888' }}>({city.sub_regions.length} sub-regions)</span>}
                      {!hasSubRegions && (city.residences || []).length > 0 && <span style={{ fontSize: 11, color: '#888' }}>({city.residences.length} residences)</span>}
                    </h4>
                    <button type="button" className="btn btn-danger" onClick={(e) => { e.stopPropagation(); removeCity(city.id); }}>
                      Delete city
                    </button>
                  </div>

                  {isExpanded && (
                    <div style={{ padding: '12px 0' }}>
                      <div className="form-group" style={{ marginBottom: 10 }}>
                        <label>City name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={city.name}
                          onChange={(e) => updateCity(city.id, (c) => ({ ...c, name: e.target.value }))}
                          placeholder="e.g. Paris, Lyon"
                        />
                      </div>

                      {hasSubRegions ? (
                        <div>
                          <label style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, display: 'block' }}>Sub-regions</label>
                          {city.sub_regions.map((sub) => {
                            const isSubExpanded = expandedSubRegion === sub.id;
                            return (
                              <section key={sub.id} className="admin-form-builder-section" style={{ marginBottom: 8 }}>
                                <div className="admin-form-builder-section-head" style={{ cursor: 'pointer' }} onClick={() => setExpandedSubRegion(isSubExpanded ? null : sub.id)}>
                                  <h5 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                                    <span style={{ fontSize: 11, color: '#999' }}>{isSubExpanded ? '\u25BC' : '\u25B6'}</span>
                                    {sub.name || '<Unnamed>'}
                                    <span style={{ fontSize: 11, color: '#888' }}>({(sub.residences || []).length} residences)</span>
                                  </h5>
                                  <button type="button" className="btn btn-danger" style={{ fontSize: 11, padding: '2px 8px' }} onClick={(e) => { e.stopPropagation(); removeSubRegion(city.id, sub.id); }}>
                                    Delete
                                  </button>
                                </div>
                                {isSubExpanded && (
                                  <div style={{ padding: '8px 0' }}>
                                    <div className="form-group" style={{ marginBottom: 6 }}>
                                      <label>Sub-region name</label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={sub.name}
                                        onChange={(e) => updateSubRegion(city.id, sub.id, (sr) => ({ ...sr, name: e.target.value }))}
                                        placeholder="e.g. PARIS, Palaiseau"
                                      />
                                    </div>
                                    <label style={{ fontSize: 12, fontWeight: 600 }}>Residences</label>
                                    <ResidencesList residences={sub.residences || []} onRemove={(idx) => removeResidenceFromSubRegion(city.id, sub.id, idx)} />
                                    <ResidenceInput inputKey={`sub_${sub.id}`} placeholder="Add residence..." onAdd={() => addResidenceToSubRegion(city.id, sub.id)} />
                                  </div>
                                )}
                              </section>
                            );
                          })}
                          <button type="button" className="btn btn-outline" style={{ marginTop: 4 }} onClick={() => addSubRegion(city.id)}>
                            Add sub-region
                          </button>
                        </div>
                      ) : (
                        <div>
                          <label style={{ fontSize: 12, fontWeight: 600 }}>Residences</label>
                          <ResidencesList residences={city.residences || []} onRemove={(idx) => removeResidenceFromCity(city.id, idx)} />
                          <ResidenceInput inputKey={`city_${city.id}`} placeholder="Add residence..." onAdd={() => addResidenceToCity(city.id)} />
                          <div style={{ marginTop: 8 }}>
                            <button type="button" className="btn btn-outline" style={{ fontSize: 12 }} onClick={() => addSubRegion(city.id)}>
                              Add sub-region (split city into zones)
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </article>
              );
            })
          )}

          <button type="button" className="btn btn-outline" onClick={addCity}>
            Add city
          </button>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Saving...' : 'Save catalog'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CitiesManager;
